import { useState } from 'react';
import { useChatStore } from '../../store/chatStore';
import { useFeedback } from '../../hooks/useFeedback';
import { X, Send, Star } from 'lucide-react';

interface CorrectionModalProps {
  messageId: string;
  onClose: () => void;
}

export function CorrectionModal({ messageId, onClose }: CorrectionModalProps) {
  const [rating, setRating] = useState(0);
  const [correction, setCorrection] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const { messages } = useChatStore();
  const { submitFeedback } = useFeedback();

  const message = messages.find((m) => m.id === messageId);
  const messageIndex = messages.findIndex((m) => m.id === messageId);
  const userMessage = messages.slice(0, messageIndex).reverse().find((m) => m.role === 'user');

  const handleSubmit = async () => {
    if (rating === 0) return;

    setIsSubmitting(true);
    try {
      await submitFeedback({
        messageId,
        query: userMessage?.content || '',
        wasHelpful: rating >= 3,
        rating,
        feedbackText: correction || undefined,
      });
      setSubmitted(true);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl p-6 max-w-md w-full text-center animate-fade-in">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-800">Thank You!</h3>
          <p className="text-gray-500 mt-1">Your feedback helps us improve.</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="correction-modal-title"
    >
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 id="correction-modal-title" className="text-lg font-semibold text-gray-800">
            Provide Feedback
          </h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Original response preview */}
          <div>
            <label className="text-sm font-medium text-gray-700">Original Response</label>
            <div className="mt-1 p-3 bg-gray-50 rounded-lg text-sm text-gray-600 max-h-24 overflow-y-auto">
              {message?.content.slice(0, 200)}
              {message && message.content.length > 200 && '...'}
            </div>
          </div>

          {/* Rating */}
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-2">
              Rate this response
            </label>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setRating(star)}
                  className={`p-1 transition-colors ${
                    star <= rating ? 'text-yellow-400' : 'text-gray-300 hover:text-yellow-300'
                  }`}
                  aria-label={`Rate ${star} stars`}
                >
                  <Star size={24} fill={star <= rating ? 'currentColor' : 'none'} />
                </button>
              ))}
            </div>
          </div>

          {/* Correction text */}
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-2">
              What should the correct answer be? (optional)
            </label>
            <textarea
              value={correction}
              onChange={(e) => setCorrection(e.target.value)}
              placeholder="Provide the correct information or explain what was wrong..."
              className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-100 focus:border-primary-300 outline-none resize-none"
              rows={4}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={rating === 0 || isSubmitting}
            className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? (
              <span className="animate-pulse">Submitting...</span>
            ) : (
              <>
                <Send size={16} />
                Submit Feedback
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
